import EditHeader from "common/components/EditHeader/EditHeader";
import HeaderButtonsEdit from "common/components/EditHeader/HeaderButtonsEdit";
import FormControl from "common/components/FormControl/FormControl";
import FormScrollError from "common/components/FormScrollError/FormScrollError";
import useRouter from "common/hooks/use-router";
import { useMemo, useState } from "react";
import { Form } from "react-final-form";
import { useTranslation } from "react-i18next";
import arrayMutators from "final-form-arrays";
import usePark from "common/hooks/usePark";
import TagSelect from "components/select/TagSelect/TagSelect";
import { Tag } from "types/Tag";
import useAuth from "common/hooks/useAuth";
import ObservationGridSelect from "components/select/ObservationGridSelect/ObservationGridSelect";

function TagForm({ onSubmit, tag }: any) {
  const { t } = useTranslation();
  const [errorMessage, setErrorMessage] = useState("");
  const { navigate } = useRouter();
  const { park: currentPark } = usePark();
  const { user: currentUser } = useAuth();
  const canManageTag = tag?.id
    ? (currentUser?.isAdmin ||
        currentUser?.isSuperadmin ||
        ((currentUser?.isManager || currentUser?.isObserver) &&
          currentUser?.administrationPermissions?.tag?.update)) ??
      false
    : (currentUser?.isAdmin ||
        currentUser?.isSuperadmin ||
        ((currentUser?.isManager || currentUser?.isObserver) &&
          currentUser?.administrationPermissions?.tag.create)) ??
      false;

  const initialValues = useMemo(
    () =>
      tag?.id
        ? {
            ...tag,
            park: tag.park?.id ?? currentPark?.id,
            childrenTags: tag.childrenTags?.map((e: Tag) => e.id),
            parentTag: tag.parentTag?.id,
            observationGrids: tag?.observationGrids?.map(
              (grid: any) => grid.id
            ),
          }
        : {
            name: "",
            park: currentPark?.id,
          },
    [tag, currentPark]
  );

  const onSubmitWrapper = async (values: any) => {
    const res = await onSubmit(values);

    if (res.message) {
      setErrorMessage(res.message);
    }
  };

  return (
    <Form
      onSubmit={onSubmitWrapper}
      subscription={{ values: true, submitting: true, pristine: true }}
      initialValues={initialValues}
      mutators={{
        ...arrayMutators,
        setFieldValue: ([field]: any, state: any, utils: any) => {
          utils.changeValue(state, field.field, () => field.value);
        },
      }}
      render={({ handleSubmit, values }) => {
        return (
          <div className="mx-auto max-w-full py-4 px-4 text-slate-500 text-base">
            {/* Header */}
            <EditHeader
              title={t(tag?.id ? "editItem.tag" : "newItem.tag")}
              subtitle={values.name}
              icon="PiTagLight"
              errorMessage={errorMessage}
              HeaderButtonsEdit={
                <HeaderButtonsEdit
                  inHeader
                  onSubmit={canManageTag ? handleSubmit : undefined}
                  onCancel={() => navigate("/tags")}
                />
              }
            />
            <form onSubmit={handleSubmit} noValidate>
              <FormScrollError />

              <div className="responsive-flex-lg gap-2 mt-2">
                <div className="lg:flex-1 flex flex-col gap-2 white-box">
                  <div className="flex flex-row gap-4">
                    <div className="flex flex-col w-full gap-2">
                      <FormControl
                        name="name"
                        title={t("common.name")}
                        required
                        editable={canManageTag}
                      />
                    </div>
                    {!values.parentTag && (
                      <div className="flex flex-col w-full gap-2">
                        <FormControl
                          name="childrenTags"
                          title={t("forms.childrenTags")}
                          type="custom"
                          editable={canManageTag}
                          // required
                        >
                          <TagSelect multiple tagToExclude={values.id} />
                        </FormControl>
                      </div>
                    )}
                    <div className="flex flex-col w-full gap-2">
                      <FormControl
                        title={t("forms.observationGrids")}
                        type="custom"
                        isSelect
                        name="observationGrids"
                        editable={canManageTag}
                        multiple
                      >
                        <ObservationGridSelect />
                      </FormControl>
                    </div>
                  </div>
                </div>
              </div>

              <HeaderButtonsEdit
                onSubmit={handleSubmit}
                onCancel={() => navigate("/tags")}
              />
            </form>
          </div>
        );
      }}
    />
  );
}

export default TagForm;
